import React from "react";

import { faNetworkWired, faPencil, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	faFolder,
	faFolderOpen,
	faHardDrive,
	faSparkles,
	faTags,
	faUser,
} from "@performyard/fontawesome/pro-solid-svg-icons";
import { Toggle } from "@performyard/py-components";

import IconButton from "../../../../components/Button/IconButton";
import Icon from "../../../../components/Icon/Icon";
import Link from "../../../../components/Link";
// components
import Typography from "../../../../components/Text";
import Tooltip from "../../../../components/Tooltip";

import { useAiSettings, useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

// styles
import css from "../..//Documents.module.scss";
// helpers
import { bytesToKB, formatCompanyTotalFileSize } from "../../helpers";
// schemas
import { DocumentFileRow, DownloadAllMyFilesRow, FolderRow, TotalSizeRow } from "../../schema";

export const Folder = (props: FolderRow) => {
	const { name, id, isOpen, toggleFolderState } = props;

	return (
		<div className={css.row}>
			<div className={css.clickable} onClick={() => toggleFolderState(id)}>
				<Icon icon={isOpen ? faFolderOpen : faFolder} className={css.folderIcon} />
				<Typography size="paragraph-3" weight="light" color="primary-5" className={css.mr8}>
					{name}
				</Typography>
			</div>
		</div>
	);
};

export const TotalSize = ({ totalSize, name, companyMaxSpace }: TotalSizeRow) => {
	return (
		<div className={css.row}>
			<Icon icon={faHardDrive} className={css.totalSizeIcon} />
			<Typography size="paragraph-3" weight="light" color="neutral-7" className={css.mr8}>
				{name} {bytesToKB(totalSize)}{" "}
				{companyMaxSpace !== undefined && `out of ${formatCompanyTotalFileSize(companyMaxSpace)}`}
			</Typography>
		</div>
	);
};

export const DownloadAllMyFiles = (props: DownloadAllMyFilesRow) => {
	const { userId } = props;
	const protocol = window.location.protocol;
	const host = window.location.host;
	const downloadMyDocumentsLink = `${protocol}//${host}/all-user-documents/${userId}/?folder=user-docs`;

	return (
		<div className={css.row}>
			<Link className={css.clickable} href={downloadMyDocumentsLink}>
				<div className={css.blankIcon} />
				<Typography size="paragraph-3" weight="light" color="primary-5" className={css.mr8}>
					Download this folder
				</Typography>
			</Link>
		</div>
	);
};

export const File = (r: DocumentFileRow) => {
	const { userData, isCompanyDocumentPage } = r;
	const { thisUserId } = userData;
	const { data } = useRbacPrimaryRolePermissions("documents");
	const editable = data?.includes("documents:editDocument") || thisUserId === r.uploader;

	// Check if AI features should be shown (requires both AI FF and Conversational AI FF)
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { data: aiSettings } = useAiSettings();
	const isAiEnabled =
		aiFeatureFlagEnabled &&
		conversationalAIFeatureFlagEnabled &&
		aiSettings?.conversationalAiSettings?.askHrEnabled;

	const showAiToggle = isCompanyDocumentPage && r.vis_to_everyone !== undefined && isAiEnabled;

	const handleAiToggle = async (checked: boolean) => {
		if (r.handleToggleAiRetrieval) {
			await r.handleToggleAiRetrieval(r, checked);
		}
	};

	return (
		<div className={css.row}>
			<div className={css.clickable} onClick={() => r.handleViewDocument(r)}>
				<div className={css.blankIcon} />
				<div className={css.filenameContainer}>
					<Typography
						size="paragraph-3"
						weight="light"
						color="primary-5"
						className={`${css.mr8} py-line-clamp-1`}
					>
						{r.filename}
					</Typography>
				</div>
			</div>
			<Typography size="paragraph-3" weight="light" color="neutral-5" className={css.mr8}>
				{bytesToKB(r.length)}
			</Typography>
			{(r.vis_to_emp || r.vis_to_everyone) && (
				<Tooltip message="Visible to employee" placement="top">
					<Icon icon={faUser} size="small" className={`${css.lightIcon} ${css.mr8}`} />
				</Tooltip>
			)}
			{r.vis_to_man && (
				<Tooltip message="Visible to managers" placement="top">
					<Icon icon={faNetworkWired} size="small" className={`${css.lightIcon} ${css.mr8}`} />
				</Tooltip>
			)}
			{r.tags?.length > 0 && (
				<div className={css.row}>
					<Icon icon={faTags} size="small" className={`${css.lightIcon} ${css.mr8}`} />
					<Typography size="paragraph-3" weight="light" color="neutral-5" className={css.mr8}>
						{r.tags.length === 1 ? r.tags : r.tags.join(", ")}
					</Typography>
				</div>
			)}
			<div className={css.spacer} />
			{showAiToggle && (
				<Tooltip message="Allow Ask PerformYard retrieval" placement="top">
					<div className={css.mr8}>
						<Toggle
							id={`ai-toggle-${r._id}`}
							size={24}
							color={r.allow_ai_retrieval ? "primary" : "neutral"}
							checked={r.allow_ai_retrieval ?? false}
							onCheckedChange={handleAiToggle}
							aria-label="Allow Ask PerformYard retrieval"
							iconTrue={faSparkles}
							iconFalse={faSparkles}
							dangerouslySetClassName="data-[state=unchecked]:bg-neutral-4 mr-4"
						/>
					</div>
				</Tooltip>
			)}
			{editable && (
				<div className={css.buttonsContainer}>
					<IconButton icon={faPencil} onClick={() => r.handleSelectEdit(r)} className={css.editIcon} />
					<IconButton icon={faTrashCan} onClick={() => r.handleSelectDelete(r)} className={css.deleteIcon} />
				</div>
			)}
		</div>
	);
};
